import React from 'react';
import { IoBatteryHalfOutline } from 'react-icons/io5';

import { isNumber } from 'radash';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import {
    ConverterLimitState,
    EXPECTED_PROTECTION_UNITS,
    FaultSimulationHelpers,
    ProtectionDeviceState,
} from '@diagram/helpers/FaultSimulationHelpers';
import { PROTECTION_STATE_COLORS } from '@diagram/helpers/ProtectionSymbolHelpers';
import { SimulationHelpers } from '@diagram/helpers/SimulationHelpers';
import { DiagramService } from '@diagram/services/DiagramService';
import { zonesState } from '@diagram/state/zones';

import {
    AcGroundingConfigurationEnum,
    CompanyProfile,
    type Component,
    currentConverter,
    type DiagramComponentInstance,
    DiagramConnection,
    energyConverter,
    FaultSimulation,
    getCurrentOSDesignation,
    i2tConverter,
    isProtectionDevice,
    MeasurementSystem,
    Simulation,
    voltageConverter,
} from 'models';
import { ComponentHelpers } from 'helpers/ComponentHelpers';
import { DateHelpers } from 'helpers/DateHelpers';
import { FormatHelpers } from 'helpers/formatters';

import { ConnectionHelpers } from '../helpers/ConnectionHelpers';
import { canvasState } from '../state/canvas';
import { ComponentInstanceService } from './ComponentInstanceService';

type SimulationFlags = {
    components?: {
        productInformation?: boolean;
    };
    simulation: {
        stateOfCharge?: boolean;
        powerLosses?: boolean;
        powerLossesInPercent?: boolean;
        touchVoltage?: boolean;
    };
};

export type ComponentInstanceLabel = {
    label: string | React.ReactNode;
    color?: string;
    bgColor?: string;
    importance?: number;
};

type StaticComponentLabelParams = {
    componentInstance: DiagramComponentInstance;
    component?: Component;
    manufacturer?: CompanyProfile;
    simulationFlags?: SimulationFlags;
    measurementSystem?: MeasurementSystem;
};

type DynamicComponentLabelParams = {
    componentInstance: DiagramComponentInstance;
    simulationResult?: Simulation['result']['componentInstances'][number];
    activeTimestamp?: number;
    simulationFlags?: SimulationFlags;
    faultSimulation?: FaultSimulation;
    allFaultSimulations?: FaultSimulation[];
    hoverTimestamp?: number | null;
};

const LabelService = {
    getComponentLabelsWithImportance: (
        params: StaticComponentLabelParams & DynamicComponentLabelParams,
    ): ComponentInstanceLabel[] =>
        [...LabelService.getStaticComponentLabels(params), ...LabelService.getDynamicComponentLabels(params)].filter(
            (item) => !!item.label,
        ),

    // Labels that do not depend on the simulation timestamp, so the canvas can build them once per change
    // of the component instead of on every hover step.
    getStaticComponentLabels: ({
        componentInstance,
        component,
        manufacturer,
        simulationFlags,
        measurementSystem,
    }: StaticComponentLabelParams): ComponentInstanceLabel[] => {
        const { label, designator, componentType } = componentInstance;
        const showLabelInIcon = ['bus', 'panel', 'custom'].includes(componentType);

        const totalComponents = ComponentInstanceHelpers.getTotalComponents(componentInstance);

        const labels: ComponentInstanceLabel[] = [];

        if (totalComponents > 1) {
            labels.push({ label: `${designator} (${totalComponents}x)`, importance: 1000 });
        } else {
            labels.push({ label: designator, importance: 1000 });
        }

        if (!showLabelInIcon) {
            labels.push({ label, importance: 0 });
        }

        if (simulationFlags?.components?.productInformation ?? true) {
            labels.push(
                ...LabelService.getProductLabels({ componentInstance, component, manufacturer }).map(
                    (productLabel) => ({
                        label: productLabel,
                        importance: 1,
                    }),
                ),
            );
        }

        labels.push(
            ...LabelService.getSpecifiactionLabels(componentInstance, measurementSystem).map((specLabel) => ({
                label: specLabel,
                importance: 500,
            })),
        );

        return labels;
    },

    // Labels that follow the active/hovered simulation timestamp.
    getDynamicComponentLabels: ({
        componentInstance,
        simulationResult,
        activeTimestamp,
        simulationFlags,
        faultSimulation,
        allFaultSimulations,
        hoverTimestamp,
    }: DynamicComponentLabelParams): ComponentInstanceLabel[] => [
        ...LabelService.getSimulationLabels({
            simulationResult,
            activeTimestamp,
            simulationFlags,
        }).map((simulationLabel) => ({
            ...simulationLabel,
            importance: 999,
        })),
        ...LabelService.getFaultSimulationLabels({
            componentInstance,
            faultSimulation,
            allFaultSimulations,
            activeTimestamp,
            hoverTimestamp,
            simulationFlags,
        }).map((faultSimLabel: { label: React.ReactNode; color?: string; importance?: number }) => ({
            ...faultSimLabel,
            importance: faultSimLabel.importance ?? 1000,
        })),
    ],

    getShownLabels: ({
        labels,
        maxLines,
    }: {
        labels: ComponentInstanceLabel[];
        maxLines?: number;
    }): ComponentInstanceLabel[] => {
        labels = labels.filter((item) => item.label !== null && item.label !== undefined);

        if (maxLines && maxLines < labels.length) {
            const sortedByImportance = labels.sort((a, b) => (b.importance ?? 0) - (a.importance ?? 0));
            const selectedLabels = sortedByImportance.slice(0, maxLines);

            const selectedLabelSet = new Set(selectedLabels.map((item) => item.label));
            const filteredLabels = labels.filter((item) => selectedLabelSet.has(item.label));

            return filteredLabels.map((item) => ({
                label: item.label,
                color: item.color,
            }));
        }

        return labels.map((item) => ({
            label: item.label,
            color: item.color,
        }));
    },

    getComponentLabel: (componentInstance: DiagramComponentInstance): string => {
        const { label, designator, componentType } = componentInstance;

        return label || designator || componentType;
    },

    getConnectionLabel: (
        connection: DiagramConnection,
        preload?: {
            from?: DiagramComponentInstance;
            to?: DiagramComponentInstance;
        },
    ): string => {
        const fromComponentInstance =
            preload?.from || ComponentInstanceService.getById(connection.from.componentInstanceId);
        const toComponentInstance = preload?.to || ComponentInstanceService.getById(connection.to.componentInstanceId);

        const { start, end } = ConnectionHelpers.getConnectionStartAndEnd({
            connection,
            from: fromComponentInstance,
            to: toComponentInstance,
        });

        return `${LabelService.getComponentLabel(start)} - ${LabelService.getComponentLabel(end)}`;
    },

    getProductLabels: ({
        componentInstance,
        component,
        manufacturer,
    }: {
        componentInstance: DiagramComponentInstance;
        component?: Component;
        manufacturer?: CompanyProfile;
    }): string[] => {
        const labels: string[] = [];

        // linked component
        if (component) {
            const uniqueName = [
                component.name,
                component.productIdentifier,
                manufacturer?.name ?? component.teamManufacturer,
            ]
                .filter(Boolean)
                .join(' • ');

            labels.push(uniqueName);

            return labels;
        }

        // not a linked component
        const uniqueName = [componentInstance.partNumber, componentInstance.manufacturer].filter(Boolean).join(' • ');
        labels.push(uniqueName);

        return labels;
    },

    getConnectionProductLabels: ({
        connection,
        cable,
        manufacturer,
    }: {
        connection: DiagramConnection;
        cable?: Component;
        manufacturer?: CompanyProfile;
    }): string[] => {
        // linked cable
        if (cable) {
            const uniqueName = [cable.name, cable.productIdentifier, manufacturer?.name ?? cable.teamManufacturer]
                .filter(Boolean)
                .join(' • ');

            return [uniqueName].filter(Boolean);
        }

        // not a linked cable
        const uniqueName = [connection.productIdentifier, connection.manufacturer].filter(Boolean).join(' • ');

        return [uniqueName].filter(Boolean);
    },

    getSpecifiactionLabels: (
        componentInstance: DiagramComponentInstance,
        measurementSystem: MeasurementSystem = MeasurementSystem.METRIC,
    ): string[] => {
        const { componentType } = componentInstance;
        const componentDefinition = ComponentInstanceHelpers.definition(componentType);

        const multiplier = ComponentInstanceHelpers.getTotalComponents(componentInstance);
        const isOnePort = componentDefinition.ports.displayAsSinglePort || componentType === 'grounding';
        const voltageType = componentInstance.configuration.ports[0]?.voltageType;

        const labels: string[] = [];
        const breakerLabels = [];

        const specifications = componentInstance.specifications?.electrical;

        if ('energyCapacity' in specifications) {
            const { energyCapacity } = specifications;

            const capacity = isNumber(energyCapacity.value)
                ? FormatHelpers.formatMeasurement(
                      {
                          value: energyCapacity.value * multiplier,
                          unit: energyCapacity.unit,
                      },
                      energyConverter,
                  )
                : '';

            if (capacity) {
                labels.push(capacity);

                return labels;
            }
        }

        if ('earthing' in specifications) {
            const { configuration, impedance } = specifications.earthing;

            const formattedImpedance =
                configuration === AcGroundingConfigurationEnum.IT ? FormatHelpers.formatResistance(impedance) : '';

            const earthingLabel = [
                FormatHelpers.formatGroundingConfiguration(configuration, measurementSystem),
                formattedImpedance,
            ]
                .filter(Boolean)
                .join(' • ');

            if (earthingLabel) {
                labels.push(earthingLabel);
            }
        }

        if (voltageType && isOnePort) {
            // @ts-ignore
            const { current, power, poles, groundingConfiguration } = specifications?.ports?.[0]?.[voltageType] || {};

            const isProtectionComponent = ComponentHelpers.isProtectionComponent(componentType);

            const showCurrent = current && isProtectionComponent;
            const showPower = power && !isProtectionComponent;
            const showPoles = poles;

            if (groundingConfiguration) {
                labels.push(groundingConfiguration);
            }

            if (showCurrent) {
                const value = current.nom ?? current.max ?? 0;
                const formattedCurrent = FormatHelpers.formatCurrent(value);

                if (value && formattedCurrent) {
                    labels.push(multiplier > 1 ? `${multiplier}x ${formattedCurrent}` : formattedCurrent);
                }
            }

            if (showPower) {
                const value = (power.nom ?? 0) * multiplier;
                const formattedPower = FormatHelpers.formatPower(value);

                if (value && formattedPower) {
                    labels.push(formattedPower);
                }
            }

            if (showPoles) {
                breakerLabels.push(poles);
            }
        }

        if ('breakerType' in specifications && specifications.breakerType) {
            breakerLabels.push(FormatHelpers.formatBreakerType(specifications.breakerType));
        }

        const currentOSDesignation = getCurrentOSDesignation(componentInstance);

        if (currentOSDesignation) {
            breakerLabels.push(currentOSDesignation);
        }

        if ('residualCurrentDevice' in specifications && specifications.residualCurrentDevice?.enabled) {
            const rcdValue = specifications.residualCurrentDevice.ratedResidualOperatingCurrent?.value;

            if (rcdValue) {
                const formattedRcd = FormatHelpers.formatCurrent(rcdValue);

                if (formattedRcd) {
                    breakerLabels.push(`RCD ${formattedRcd}`);
                }
            }
        }

        if (breakerLabels.length) {
            labels.push(breakerLabels.join(' • '));
        }

        return labels;
    },

    getSimulationLabels: ({
        simulationResult,
        activeTimestamp,
        simulationFlags,
    }: {
        simulationResult?: Simulation['result']['componentInstances'][number];
        activeTimestamp?: number;
        simulationFlags?: SimulationFlags;
    }): Array<{ label: React.ReactNode; color?: string }> => {
        const labels: Array<{ label: React.ReactNode; color?: string }> = [];

        if (simulationResult && isNumber(activeTimestamp)) {
            const stateOfChargeLabel = SimulationHelpers.getStateOfCharge(simulationResult, activeTimestamp);

            if (stateOfChargeLabel && simulationFlags?.simulation?.stateOfCharge) {
                labels.push({
                    label: (
                        <>
                            <IoBatteryHalfOutline /> {stateOfChargeLabel}
                        </>
                    ),
                    color: SimulationHelpers.getStateOfChargeColor(simulationResult, activeTimestamp),
                });
            }

            const powerLosses = SimulationHelpers.getPowerLosses(
                simulationResult,
                activeTimestamp,
                simulationFlags?.simulation?.powerLossesInPercent,
            );

            if (
                powerLosses &&
                (simulationFlags?.simulation?.powerLosses || simulationFlags?.simulation?.powerLossesInPercent)
            ) {
                labels.push({
                    label: powerLosses,
                    color: 'var(--mantine-color-red-9)',
                });
            }
        }

        return labels;
    },

    getFaultSimulationLabels: ({
        componentInstance,
        faultSimulation,
        allFaultSimulations = [],
        activeTimestamp,
        hoverTimestamp,
        simulationFlags,
    }: {
        componentInstance: DiagramComponentInstance;
        faultSimulation?: FaultSimulation;
        allFaultSimulations?: FaultSimulation[];
        activeTimestamp?: number;
        hoverTimestamp?: number | null;
        simulationFlags?: SimulationFlags;
    }) => {
        if (!faultSimulation) return [];

        const labels: ComponentInstanceLabel[] = [];

        if (
            (simulationFlags?.simulation?.touchVoltage ?? true) &&
            isNumber(activeTimestamp) &&
            FaultSimulationHelpers.isLineToEarthFault(faultSimulation.faultLines)
        ) {
            const touchVoltage = FaultSimulationHelpers.getComponentTouchVoltageAt(
                faultSimulation,
                componentInstance.id,
                activeTimestamp,
            );

            if (isNumber(touchVoltage)) {
                // Ranks below the protection readings: a crowded device has room for three lines and
                // no tooltip once any are dropped, and its own trip verdict is the one that cannot be
                // read off a neighbour.
                labels.push({
                    label: `PE ${FormatHelpers.formatMeasurement({ value: touchVoltage, unit: 'V' }, voltageConverter)}`,
                    color: 'var(--mantine-color-orange-6)',
                    importance: 999,
                });
            }
        }

        if (!isProtectionDevice(componentInstance.componentType)) return labels;

        const diagram = DiagramService.get();

        const faultConnection = diagram.connections[faultSimulation.faultConnectionId];
        const isOnFaultConnection =
            faultConnection &&
            (faultConnection.from.componentInstanceId === componentInstance.id ||
                faultConnection.to.componentInstanceId === componentInstance.id);

        if (isOnFaultConnection) {
            const criticalClearingTime = FaultSimulationHelpers.calculateCriticalClearingTime(
                diagram,
                zonesState.zones.fault,
                zonesState.zones.protection,
                faultSimulation,
                allFaultSimulations,
            );

            if (isNumber(criticalClearingTime?.criticalClearingTime)) {
                labels.push({
                    label: DateHelpers.formatSeconds(criticalClearingTime.criticalClearingTime),
                    color: 'var(--mantine-color-orange-6)',
                });
            }
        }

        if (componentInstance.componentType === 'fuse' && isNumber(hoverTimestamp)) {
            const time = faultSimulation.result.time;
            const faultTime = faultSimulation.faultTime;

            if (time) {
                const faultTimeIndex = time.findIndex((t) => t >= faultTime);
                const hoverTimeIndex = time.findIndex((t) => t >= hoverTimestamp);

                if (faultTimeIndex !== -1 && hoverTimeIndex !== -1 && hoverTimeIndex > faultTimeIndex) {
                    const result = FaultSimulationHelpers.calculateFuseI2t({
                        simulation: faultSimulation,
                        fuse: componentInstance,
                        endTimeIndex: hoverTimeIndex,
                    });

                    if (isNumber(result?.i2t)) {
                        const formatI2t = (value: number) => FormatHelpers.formatValue(value, i2tConverter);

                        const scenarios = faultSimulation.result.scenarios;
                        const i2tRange = SimulationHelpers.getScenarioRange(
                            {
                                min: scenarios?.min
                                    ? FaultSimulationHelpers.calculateFuseI2t({
                                          simulation: faultSimulation,
                                          fuse: componentInstance,
                                          endTimeIndex: hoverTimeIndex,
                                          scenario: 'min',
                                      })
                                    : undefined,
                                max: scenarios?.max
                                    ? FaultSimulationHelpers.calculateFuseI2t({
                                          simulation: faultSimulation,
                                          fuse: componentInstance,
                                          endTimeIndex: hoverTimeIndex,
                                          scenario: 'max',
                                      })
                                    : undefined,
                            },
                            (scenarioResult) => scenarioResult?.i2t,
                        );

                        labels.push({
                            label: SimulationHelpers.withScenarioRange(formatI2t(result.i2t), i2tRange, formatI2t),
                            color: 'var(--mantine-color-orange-6)',
                        });
                    }
                }
            }
        }

        return labels;
    },

    showLongLabelSuggestion: () => {
        canvasState.labels.suggestions.labels = DiagramService.getId();
    },
};

const formatProtectionMeasurement = (state: ProtectionDeviceState, value?: number, unit?: string) => {
    if (!isNumber(value)) return null;

    if (unit && unit !== EXPECTED_PROTECTION_UNITS[state.metric]) return `${value} ${unit}`;

    if (state.metric === 'i2t') return FormatHelpers.formatValue(value, i2tConverter);

    if (state.metric === 'voltage') return FormatHelpers.formatValue(value, voltageConverter, 'V');

    return FaultSimulationHelpers.formatFaultCurrent(value);
};

/**
 * Which protection a converter left its declared limit against, in the abbreviations a power
 * engineer reads off a datasheet. The threshold follows because that is the setting the trip
 * is against, and the 56 px label box has room for it but not for the words.
 */
const CONVERTER_LIMIT_LABELS: Record<ConverterLimitState['kind'], string> = {
    undervoltage: 'UVP',
    overvoltage: 'OVP',
    overcurrent: 'OCP',
};

export const formatConverterLimitLabel = (state: ConverterLimitState): ComponentInstanceLabel => {
    const threshold =
        state.kind === 'overcurrent'
            ? FormatHelpers.formatMeasurement({ value: state.threshold, unit: 'A' }, currentConverter)
            : FormatHelpers.formatMeasurement({ value: state.threshold, unit: 'V' }, voltageConverter);

    return {
        label: `${CONVERTER_LIMIT_LABELS[state.kind]} ${threshold}`,
        color: PROTECTION_STATE_COLORS.tripped,
    };
};

export const formatProtectionStateLabel = (state: ProtectionDeviceState): ComponentInstanceLabel => {
    const value = formatProtectionMeasurement(state, state.value, state.valueUnit);
    const threshold = formatProtectionMeasurement(state, state.threshold, state.thresholdUnit);
    const margin = value && threshold ? ` ${value} / ${threshold}` : ' (margin not reported)';
    const reason = state.reason ? ` (${state.reason.replaceAll('-', ' ')})` : '';
    const units = state.unitMismatch ? ' (unexpected units)' : '';

    const color = PROTECTION_STATE_COLORS[state.status];

    if (state.status === 'not-reported') {
        return { label: 'No trip result reported', color };
    }

    if (state.status === 'not-modelled') {
        return { label: `Trip not modelled${reason}`, color };
    }

    if (state.status === 'inconclusive') {
        return { label: `Trip inconclusive, solve truncated${margin}${reason}${units}`, color };
    }

    if (state.status === 'did-not-trip') {
        return { label: `Did not trip${margin}${reason}${units}`, color };
    }

    if (state.status === 'tripped-time-unknown' || !isNumber(state.tripTime)) {
        return { label: 'Tripped, trip time unavailable', color };
    }

    return state.open
        ? { label: `Tripped ${DateHelpers.formatSeconds(state.tripTime)}`, color }
        : { label: `Trips at ${DateHelpers.formatSeconds(state.tripTime)}`, color };
};

export { LabelService };
